import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Stack,
  Typography,
} from "@mui/material";
import { AutoAwesome, Article } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { RequestFile, SignRequest } from "models/interfaces";
import {
  fetchDocumentSummary,
  fetchDocumentSummaryCallback,
} from "../SignRequestService";
import { handleErrorMessage } from "helpers/methods";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import AIDisclaimer from "./AIDisclaimer";
import { convertFileForBackend } from "screens/Shared/CommonHelper";

interface ContractGlanceProps {
  signRequest: SignRequest;
  isPublic?: boolean;
  requesToken?: string;
  hideHeader?: boolean;
}

const formatLabel = (key: string) =>
  key
    .replace(/_/g, " ")
    // Split camelCase / PascalCase (e.g. "TerminationDate" → "Termination Date").
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(" ");

/**
 * The backend sometimes nests the extracted terms under a wrapper key (e.g.
 * `ContractAtGlance` / `contract_at_a_glance`) and mixes in a plain-language
 * `summary`. Flatten one level of nested objects and drop `summary`, so the
 * view renders a single clean list of contract terms.
 */
const flattenGlanceTerms = (data: Record<string, any>): [string, any][] => {
  const terms: [string, any][] = [];
  Object.entries(data || {}).forEach(([key, value]) => {
    if (key.toLowerCase() === "summary") return;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (nestedKey.toLowerCase() === "summary") return;
        terms.push([nestedKey, nestedValue]);
      });
    } else {
      terms.push([key, value]);
    }
  });
  return terms;
};

const renderValue = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return (
      <Typography
        variant="body2"
        sx={{ color: "#646464", fontStyle: "italic", fontSize: "0.875rem" }}
      >
        Not specified
      </Typography>
    );
  }
  if (Array.isArray(value)) {
    return (
      <Box component="ul" sx={{ pl: 2.5, m: 0, listStyle: "none" }}>
        {value.map((item, idx) => (
          <Box
            component="li"
            key={idx}
            sx={{
              mb: 0.5,
              pl: 1.5,
              position: "relative",
              "&::before": {
                content: '"•"',
                position: "absolute",
                left: 0,
                color: "#646464",
                fontSize: "1.2rem",
                lineHeight: "1.5",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.6 }}
            >
              {typeof item === "object" ? JSON.stringify(item) : String(item)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  if (typeof value === "object") {
    return (
      <Stack spacing={1} sx={{ pl: 1 }}>
        {Object.entries(value).map(([k, v]) => (
          <Box key={k}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.8rem",
                color: "#555",
                mb: 0.25,
              }}
            >
              {formatLabel(k)}
            </Typography>
            {renderValue(v)}
          </Box>
        ))}
      </Stack>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{ fontSize: "0.875rem", color: "#444", lineHeight: 1.6 }}
    >
      {String(value)}
    </Typography>
  );
};

const ContractGlance = ({
  signRequest,
  isPublic,
  requesToken,
  hideHeader,
}: ContractGlanceProps) => {
  const [selectedDocument, setSelectedDocument] = useState<RequestFile | null>(
    null,
  );
  const [glance, setGlance] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(false);

  const documentOptions = useMemo(() => {
    return signRequest.files.map((file: RequestFile, index: number) => {
      const fileName =
        file.unsignedDoc || file.unsignedPdf || `Document ${index + 1}`;
      return {
        id: index,
        file,
        label: fileName,
        value: fileName,
      };
    });
  }, [signRequest.files]);

  const selectedOption = useMemo(() => {
    if (!selectedDocument) return null;
    const selectedValue =
      selectedDocument.unsignedDoc || selectedDocument.unsignedPdf;
    if (!selectedValue) return null;
    return (
      documentOptions.find(
        (opt: { value: string }) => opt.value === selectedValue,
      ) || null
    );
  }, [selectedDocument, documentOptions]);

  const handleDocumentChange = (_event: any, newValue: any) => {
    if (newValue && typeof newValue === "object" && newValue.file) {
      setSelectedDocument(newValue.file);
      setGlance(null);
    } else if (newValue === null || newValue === undefined) {
      setSelectedDocument(null);
      setGlance(null);
    }
  };

  const getContractGlance = async () => {
    if (!selectedDocument) return;
    try {
      setLoading(true);
      setGlance(null);
      const file = convertFileForBackend(selectedDocument);
      const response: any = await (isPublic
        ? fetchDocumentSummaryCallback(requesToken!, file)
        : fetchDocumentSummary(signRequest.id, file));
      if (response) {
        // Core returns the glance under `contractAtGlance` (keys like
        // "effective date", "parties", …) next to the plain-language `summary`
        // and, when the AI could not finish the document, an `error`. Use the
        // wrapper when present (either spelling); otherwise treat the whole
        // response as the glance data, so results still render even when every
        // extracted field is null.
        const glanceData =
          typeof response === "object" &&
          (response.contractAtGlance || response.contract_at_a_glance)
            ? response.contractAtGlance ?? response.contract_at_a_glance
            : response;
        if (glanceData && typeof glanceData === "object") {
          // The flat fallback also carries `summary`, `documentId`, `error` and
          // `terminationDate`; the glance view shows only the extracted contract
          // fields, so drop those here.
          const contractFields: Record<string, any> = { ...glanceData };
          delete contractFields.summary;
          delete contractFields.documentId;
          delete contractFields.error;
          delete contractFields.terminationDate;
          setGlance(contractFields);
        } else {
          setGlance(null);
        }
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <Stack
        direction="column"
        display="flex"
        justifyContent="space-between"
        sx={{ height: "100%", minHeight: 0 }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!hideHeader && (
            <Box
              sx={{
                color: "primary.main",
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Article sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight={600}>
                Contract at a Glance
              </Typography>
            </Box>
          )}

          {selectedDocument && (
            <Box
              sx={{
                color: "primary.main",
                px: 1.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                <Box component="span" aria-hidden="true">
                  📄
                </Box>
                {selectedDocument.unsignedDoc ||
                  selectedDocument.unsignedPdf ||
                  "Document"}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              backgroundColor: "#fafafa",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d0d0d0",
                borderRadius: "3px",
                "&:hover": { backgroundColor: "#b0b0b0" },
              },
            }}
          >
            {/* Announce readiness for screen-reader users (WCAG 4.1.3). */}
            <Box
              component="span"
              role="status"
              aria-live="polite"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                clipPath: "inset(50%)",
              }}
            >
              {glance ? "Analysis ready" : ""}
            </Box>
            {glance ? (
              <Box sx={{ p: 2, textAlign: "left" }}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    border: "1px solid #ececec",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  {flattenGlanceTerms(glance).map(([key, value], idx, arr) => (
                    <Box
                      key={`${key}-${idx}`}
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom:
                          idx < arr.length - 1 ? "1px solid #f2f2f2" : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          color: "#8a8a8a",
                          mb: 0.5,
                        }}
                      >
                        {formatLabel(key)}
                      </Typography>
                      {renderValue(value)}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : selectedDocument && !loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  opacity: 0.7,
                }}
              >
                <ErrorOutlineIcon
                  sx={{ fontSize: "3rem", color: "#646464", mb: 2 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: "#646464", fontWeight: 500 }}
                >
                  Select a document and click Analyze
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <Article sx={{ color: "white", fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{
                    textAlign: "center",
                    mb: 1,
                    fontWeight: 600,
                    fontSize: "1.125rem",
                  }}
                >
                  Check key terms
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                    maxWidth: 280,
                    px: 2,
                  }}
                >
                  Pick a document below to pull out parties, term, renewal, and
                  other key contract terms.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            pt: 2,
            pb: 1.5,
            px: 2,
            flexShrink: 0,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "white",
          }}
        >
          <Stack spacing={1.5}>
            <CustomAutoComplete
              id="glance-document-autocomplete"
              label="Select document"
              placeHolder="Choose a document..."
              options={documentOptions}
              value={selectedOption}
              onChange={handleDocumentChange}
              getOptionLabel={(option: any) =>
                typeof option === "string"
                  ? option
                  : option?.label || option?.value || ""
              }
              freeSolo={false}
              clearIcon={true}
              filterOptions={(options: any[]) => options}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingRight: "56px !important",
                  "& fieldset": {
                    borderColor: "#757575",
                    borderWidth: "1.5px",
                  },
                },
                "& .MuiAutocomplete-endAdornment": {
                  right: 6,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.875rem",
                  minWidth: 0,
                  textOverflow: "ellipsis",
                },
              }}
              textFieldVariant="outlined"
            />
            <Button
              variant="contained"
              fullWidth
              onClick={getContractGlance}
              disabled={!selectedDocument || loading}
              startIcon={
                loading ? (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    sx={{ color: "gray" }}
                  />
                ) : (
                  <AutoAwesome sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                py: 1.25,
                minHeight: 44,
                borderRadius: 1.5,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Analyzing..." : "Extract key terms"}
            </Button>
          </Stack>
          <AIDisclaimer />
        </Box>
      </Stack>
    </Fade>
  );
};

export default ContractGlance;
